import { useState, useEffect, useCallback } from 'react';
import { getOrderWithRelation } from '@/api/order-api';
import { OrderResponse, OrderItemResponse, PaymentRecord } from '@/types/order-type';
import { Fulfillment } from '@/types/fulfillment-type';
import { Customer } from '@/types/customer-type';
import { getAuthUser } from '@/lib/auth-utils';
import { User } from '@/types/auth';

interface UsePublicOrderDetailsDataReturn {
  order: OrderResponse | null;
  customer: Customer | null;
  orderItems: OrderItemResponse[];
  fulfillment: Fulfillment | null;
  payments: PaymentRecord[];
  isLoading: boolean;
  error: Error | string | null;
  refresh: (silent?: boolean) => void;
  user: User | null;
}

export function usePublicOrderDetailsData(orderId: string | null): UsePublicOrderDetailsDataReturn {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItemResponse[]>([]);
  const [fulfillment, setFulfillment] = useState<Fulfillment | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(!!orderId);
  const [error, setError] = useState<Error | string | null>(null);
  const user = getAuthUser();

  const fetchOrder = useCallback(async (silent = false) => {
    if (!orderId) return;

    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const orderWithRelation = await getOrderWithRelation(orderId);
      if (orderWithRelation.success) {
        setOrder(orderWithRelation.data.order);
        setCustomer(orderWithRelation.data.customer);
        setPayments(orderWithRelation.data.payments || []);
        setOrderItems(orderWithRelation.data.orderItems);
        setFulfillment(orderWithRelation.data.fulfillment || null);
      } else if (!silent) {
        setError(orderWithRelation.message || 'Failed to load order');
      }
    } catch (err: unknown) {
      if (silent) return;
      if (err instanceof Error) {
        console.error('Error fetching order:', err.message);
        setError(err);
      } else {
        console.error('Unknown error:', err);
        setError(new Error('An unexpected error occurred'));
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [orderId]);

  const refresh = useCallback((silent = false) => {
    fetchOrder(silent);
  }, [fetchOrder]);

  useEffect(() => {
    if (!orderId) return;
    refresh();
  }, [orderId, refresh]);

  return {
    order,
    customer,
    orderItems,
    fulfillment,
    payments,
    isLoading,
    error,
    refresh,
    user,
  };
}
